import { useRef, useMemo, useCallback, useState } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { isAndroid, isFirefox } from 'react-device-detect'
import { Thumbnail } from '@/components'
import { ArtworkType } from '@/@types/microcms-type'
import { useScreenSize } from '@/hooks'
import { throttle } from '@/libs/utils'

type ColProps = {
    projects: ArtworkType[]
    totalHeight: number
    position: THREE.Vector3
}

interface ImageMaterials {
    positions: number[]
    isVideo: boolean[]
    scales: [number, number][]
    urls: string[]
    posterUrls: (string | undefined)[]
}

export const Col = ({ projects, totalHeight, position }: ColProps) => {
    const {
        viewport: { width, height },
        size: { height: pxHeight }
    } = useThree()

    const screenSize = useScreenSize()
    const [loadedThumbnails, setLoadedThumbnails] = useState<Set<number>>(
        new Set([0, 1, 2, 3, 4, 5])
    )

    const w = screenSize.LG
        ? 1 / 3
        : screenSize.MD
        ? 1 / 3
        : screenSize.SM
        ? 1 / 2
        : 1

    const imageMaterials = projects.reduce(
        (data: ImageMaterials, project, index) => {
            const thumbImage = project.thumbnail_image
            // videoTexture doesn't work on Android/FireFox
            const thumbVideo =
                !(isAndroid && isFirefox) && project.thumbnail_video?.video
            const posterImage = project.thumbnail_video?.poster

            const thumbnail = posterImage ?? thumbImage

            let url = ''
            let posterUrl = undefined

            if (thumbVideo) {
                url = thumbVideo.url
                posterUrl =
                    posterImage?.url !== undefined
                        ? posterImage?.url + '?w=640&q=10&fm=webp'
                        : undefined
            } else if (thumbImage) {
                url = screenSize.LG
                    ? thumbImage?.url + '?w=750&q=70&fm=webp'
                    : screenSize.MD
                    ? thumbImage?.url + '?w=640&q=70&fm=webp'
                    : screenSize.SM
                    ? thumbImage?.url + '?w=640&q=70&fm=webp'
                    : thumbImage?.url + '?w=640&q=60&fm=webp'
            }

            const scale = [
                width * w,
                width * w * (thumbnail?.height! / thumbnail?.width!)
            ] as [number, number]

            const len = projects.length
            const prevProject = projects[(index - 1) % len] as
                | ArtworkType
                | undefined

            const prevThumbnail =
                prevProject?.thumbnail_video?.poster ||
                prevProject?.thumbnail_image ||
                undefined
            const prevPosition = data.positions[index - 1] || 0
            const preImageHight =
                (width * w * prevThumbnail?.height!) / prevThumbnail?.width! ||
                0
            const currentImageHalfHeight =
                (width * w * thumbnail?.height!) / thumbnail?.width! / 2

            const MARGIN_BOTTOM = screenSize.XS ? width * 0.3 : width * 0.005 // add margin bottom for texts area on mobile
            const position =
                prevPosition -
                preImageHight / 2 -
                currentImageHalfHeight -
                MARGIN_BOTTOM

            data.isVideo.push(Boolean(thumbVideo))
            data.urls.push(url)
            data.posterUrls.push(posterUrl)
            data.scales.push(scale)
            data.positions.push(position)

            return data
        },
        { isVideo: [], urls: [], posterUrls: [], scales: [], positions: [] }
    )

    const ref = useRef<THREE.Group>(null)
    const camera = useThree((state) => state.camera)
    const frustum = useMemo(() => new THREE.Frustum(), [])
    const worldViewPortHeight = useMemo(() => {
        return (totalHeight * height) / pxHeight
    }, [totalHeight, height, pxHeight])

    const updateFrustum = useCallback(() => {
        // update frustum
        camera.updateMatrix()
        camera.updateMatrixWorld()
        const matrix = new THREE.Matrix4().multiplyMatrices(
            camera.projectionMatrix,
            camera.matrixWorldInverse
        )
        frustum.setFromProjectionMatrix(matrix)
    }, [camera, frustum])

    const updateThumbnailPosition = useCallback(() => {
        const group = ref.current!.children
        const newLoadedThumbnails = new Set(loadedThumbnails)

        for (let i = 0; i < group.length; i++) {
            const thumbImage = group[i].children[0]
            if (!thumbImage) return
            const center = new THREE.Vector3()
            const worldPos = thumbImage.getWorldPosition(center)

            const isInViewport = frustum.intersectsObject(thumbImage)
            const isNearViewport = Math.abs(worldPos.y) < height * 2.0

            if ((isInViewport || isNearViewport) && !loadedThumbnails.has(i)) {
                newLoadedThumbnails.add(i)
            }

            if (worldPos.y < 0) {
                if (!frustum.intersectsObject(thumbImage)) {
                    group[i].position.y += worldViewPortHeight
                }
            } else {
                if (!frustum.intersectsObject(thumbImage)) {
                    group[i].position.y -= worldViewPortHeight
                }
            }
        }

        if (newLoadedThumbnails.size !== loadedThumbnails.size) {
            setLoadedThumbnails(newLoadedThumbnails)
        }
    }, [frustum, worldViewPortHeight, loadedThumbnails, height])

    const throttledUpdate = useMemo(
        () =>
            throttle(() => {
                if (!ref.current) return
                updateFrustum()
                updateThumbnailPosition()
            }, 150),
        [updateFrustum, updateThumbnailPosition]
    )

    useFrame(() => {
        // update frustum and thumbnail position
        throttledUpdate()
    })

    const { positions, isVideo, scales, urls, posterUrls } = imageMaterials

    return (
        <group position={position} ref={ref}>
            {projects.map((project, index) => (
                <Thumbnail
                    key={index}
                    project={project}
                    position={[0, positions[index], 0]}
                    isVideo={isVideo[index]}
                    scale={scales[index]}
                    url={urls[index]}
                    posterUrl={posterUrls[index]}
                    shouldLoad={loadedThumbnails.has(index)}
                />
            ))}
        </group>
    )
}
